import 'react-international-phone/style.css'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  defaultCountries,
  FlagImage,
  usePhoneInput
} from 'react-international-phone'

import { useClickOutside } from '@toasttab/use-click-outside'
import classnames from 'classnames'
import {
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumber
} from 'libphonenumber-js'

import { SUPPORTED_COUNTRIES } from 'src/shared/js/phoneUtils'

import DropdownArrow from 'shared/components/common/dropdown/DropdownArrow'
import { useOptionalRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import InputMessage from './InputMessage'
import { RestartIcon } from '@toasttab/buffet-pui-icons'
import { t } from 'src/translations/src'

type InternationalPhoneInputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  value?: string
  id: string
  label?: React.ReactNode | string
  required?: boolean
  warning?: string
  unregisterOnUnmount?: boolean
  disabled?: boolean
  initialFocus?: boolean
  focusMessage?: string
  shouldUnFocus?: boolean
  isSingletonField?: boolean
  classNames?: string
  onRetry?: () => void
}

// Normalizes a phone string to E.164. Returns '' if the string is not a valid
// phone number.
//
// When region is provided, it is tried first. The unconstrained +${raw} parse is
// then blocked — this prevents partial national digit sequences from being misparsed
// as a foreign number (e.g. 9 digits of a 469 area code resolving as Swedish +46).
// When region is omitted, the unconstrained parse always runs, which allows stored
// values without a '+' prefix (e.g. '353871234567') to be resolved.
// raw values that already start with '+' always bypass the region hint per
// libphonenumber-js behaviour, so cross-country autofill resolves correctly regardless.
export const normalizeToE164 = (
  raw: string | undefined,
  region?: CountryCode
): string => {
  if (!raw) return ''
  if (region && isValidPhoneNumber(raw, region)) {
    return parsePhoneNumber(raw, region).number
  }
  const withPlus = raw.startsWith('+') ? raw : `+${raw}`
  if ((raw.startsWith('+') || !region) && isValidPhoneNumber(withPlus)) {
    return parsePhoneNumber(withPlus).number
  }
  return ''
}

export const InternationalPhoneInput: React.FC<
  InternationalPhoneInputProps
> = ({
  value,
  id,
  label,
  required,
  warning,
  unregisterOnUnmount = true,
  disabled = false,
  initialFocus = false,
  focusMessage,
  shouldUnFocus = true,
  isSingletonField = false,
  classNames,
  onRetry,
  ...inputProps
}) => {
  const ooRestaurant = useOptionalRestaurant()?.ooRestaurant
  const restaurantCountry = ooRestaurant?.i18n?.country?.toUpperCase() as
    | CountryCode
    | undefined
  const phoneNum = useMemo(
    () => normalizeToE164(value, restaurantCountry),
    [value, restaurantCountry]
  )
  const fieldName = inputProps.name ?? id
  const [controlledValue, setControlledValue] = useState(phoneNum)
  useEffect(() => setControlledValue(phoneNum), [phoneNum])
  const {
    register,
    formState: { errors },
    setValue,
    unregister,
    resetField
  } = useFormContext()
  const { inputValue, phone, handlePhoneValueChange, country, setCountry } =
    usePhoneInput({
      defaultCountry: ooRestaurant?.i18n?.country?.toLowerCase() || 'us',
      value: controlledValue,
      countries: defaultCountries,
      disableDialCodeAndPrefix: true,
      onChange: ({ phone, country }) => {
        // Keep controlledValue in sync with the library's actual phone state so
        // that a subsequent autofill (after manual deletion) registers as a real
        // state change and triggers the library's value-sync effect.
        setControlledValue(phone)
        if (
          isValidPhoneNumber(phone, country.iso2.toUpperCase() as CountryCode)
        ) {
          setValue(fieldName, phone, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          })
        }
      }
    })
  const [showDropdown, setShowDropdown] = useState(false)
  const [focus, setFocus] = useState(initialFocus)
  const dropdownRef = useClickOutside<HTMLDivElement>(() =>
    setShowDropdown(false)
  )
  const optionRefs = useRef<(HTMLLIElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState(-1)
  useEffect(() => {
    // Blur the input on mobile scroll to trigger form validation
    const handleTouchMove = () => {
      if (!isSingletonField) document.getElementById(id)?.blur()
    }
    document.addEventListener('touchmove', handleTouchMove)
    return () => document.removeEventListener('touchmove', handleTouchMove)
  }, [isSingletonField, id])

  useEffect(() => {
    // The timeout is necessary to prevent the onBlur from firing and putting us into an error state prematurely
    setTimeout(() => {
      if (initialFocus) {
        document.getElementById(id)?.focus()
      }
    }, 100)
  }, [initialFocus, id])

  const validate = useCallback(
    (value: string) =>
      (value || required) && !isValidPhoneNumber(value)
        ? 'Please enter a valid phone number'
        : true,
    [required]
  )

  useEffect(() => {
    return unregisterOnUnmount
      ? () => {
          unregister(fieldName)
        }
      : () => {}
  }, [register, required, unregister, unregisterOnUnmount, validate, fieldName])

  const onBlurInput = () => {
    // This prevents us from showing an error state on blur when the form essentially wasn't touched,
    // e.g. in the case of the auth modal where the phone input is the only field and the guest clicks
    // the close button without attempting to enter phone number.
    // or when selecting different country
    if (isSingletonField && !inputValue.replace(/\D/g, '')) {
      return
    }

    setValue(fieldName, phone, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    })

    if (shouldUnFocus) {
      setFocus(false)
    }
  }

  const selectCountry = useCallback(
    (index: number) => {
      const supportedCountry = SUPPORTED_COUNTRIES[index]

      if (supportedCountry) {
        setCountry(supportedCountry.iso2)
        resetField(fieldName)
        setShowDropdown(false)
        setFocusedIndex(-1)
        // Move focus to phone input after selection
        document.getElementById(id)?.focus()
      }
    },
    [setCountry, resetField, fieldName, id]
  )

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent) => {
      const handledKeys = [
        'ArrowUp',
        'ArrowDown',
        'Enter',
        ' ',
        'Escape',
        'Tab'
      ]
      if (!handledKeys.includes(event.key)) return

      event.preventDefault()
      event.stopPropagation()

      const lastIndex = SUPPORTED_COUNTRIES.length - 1

      switch (event.key) {
        case 'ArrowUp': {
          const newIndex = focusedIndex <= 0 ? lastIndex : focusedIndex - 1
          setFocusedIndex(newIndex)
          optionRefs.current[newIndex]?.focus()
          break
        }
        case 'ArrowDown': {
          const newIndex = focusedIndex >= lastIndex ? 0 : focusedIndex + 1
          setFocusedIndex(newIndex)
          optionRefs.current[newIndex]?.focus()
          break
        }
        case 'Enter':
        case ' ':
          if (focusedIndex >= 0) {
            selectCountry(focusedIndex)
          }
          break
        case 'Escape':
          setShowDropdown(false)
          setFocusedIndex(-1)
          // Move focus back to the button
          dropdownRef.current?.querySelector('button')?.focus()
          break
        case 'Tab':
          setShowDropdown(false)
          setFocusedIndex(-1)
          if (event.shiftKey) {
            // Shift+Tab: return focus to the combobox button
            dropdownRef.current?.querySelector('button')?.focus()
          } else {
            // Tab: move focus to the input field
            document.getElementById(id)?.focus()
          }
          break
        default:
          break
      }
    },
    [dropdownRef, focusedIndex, id, selectCountry]
  )

  // Focus the current selected option when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      const currentIndex = SUPPORTED_COUNTRIES.findIndex(
        (c) => c.iso2 === country.iso2
      )
      const indexToFocus = currentIndex >= 0 ? currentIndex : 0
      setFocusedIndex(indexToFocus)
      optionRefs.current[indexToFocus]?.focus()
    } else {
      setFocusedIndex(-1)
    }
  }, [showDropdown, country.iso2, id])

  return (
    <div
      className={classnames(
        'formInputContainer intlPhoneInput',
        errors[fieldName] && 'inputError',
        (disabled || inputProps.readOnly) && 'inputDisabled',
        classNames
      )}
    >
      <div className='inputFieldInner'>
        <div ref={dropdownRef} className='intlPhoneInputDropdown'>
          <button
            onClick={() => setShowDropdown((prevState) => !prevState)}
            role='combobox'
            type='button'
            value={country.name}
            aria-controls={`${id}-country-listbox`}
            aria-haspopup='listbox'
            disabled={disabled || inputProps.readOnly}
            aria-expanded={showDropdown}
            aria-label={`${t('customer-info-section.country-code')}: ${
              country.name
            } (+${country.dialCode})`}
          >
            <FlagImage iso2={country.iso2} />
            <DropdownArrow />
          </button>
          {showDropdown && (
            <ul
              id={`${id}-country-listbox`}
              role='listbox'
              className={showDropdown && 'active'}
              aria-label={t('customer-info-section.select-country-code')}
              tabIndex={0}
              onKeyDown={handleKeydown}
            >
              {SUPPORTED_COUNTRIES.map((supportedCountry, index) => {
                return (
                  <li
                    role='option'
                    id={`${id}-option-${supportedCountry.iso2}`}
                    tabIndex={focusedIndex === index ? 0 : -1}
                    aria-selected={country.iso2 === supportedCountry.iso2}
                    aria-label={`${supportedCountry.name} (+${supportedCountry.dialCode})`}
                    key={supportedCountry.iso2}
                    ref={(el) => (optionRefs.current[index] = el)}
                    onClick={() => selectCountry(index)}
                  >
                    <FlagImage iso2={supportedCountry.iso2} />
                    {supportedCountry.name}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <label>
          <div className='dialCodeInputRow'>
            <span className='dialCodePrefix' aria-hidden='true'>
              +{country.dialCode}
            </span>
            <input
              {...register(fieldName, {
                required: required ? 'required' : false,
                validate
              })}
              aria-required={required}
              aria-haspopup='false' // Prevent false popup announcements
              type='tel'
              autoComplete='tel'
              value={inputValue}
              placeholder=' '
              id={id}
              disabled={disabled}
              data-testid={`input-${id}`}
              onFocus={() => setFocus(true)}
              onChange={(e) => {
                const newDigitCount = e.target.value.replace(/\D/g, '').length
                const prevDigitCount = inputValue.replace(/\D/g, '').length
                const isAutofill = newDigitCount - prevDigitCount > 1

                // Autofill and paste deliver a complete number in one event, so
                // we first try an unconstrained parse (handles full international
                // numbers like +16175551234 or 16175551234).
                let e164 = ''
                if (isAutofill) {
                  e164 = normalizeToE164(e.target.value)
                }
                // If unconstrained parse fails (e.g. nationally-formatted value like
                // (617) 555-1234), retry with the selected country as region hint.
                // Manual keystrokes always take this path — passing region blocks the
                // unconstrained parse and prevents partial digit sequences from being
                // misparsed as a foreign number (e.g. 9 digits of 469 → Swedish +46).
                if (!e164) {
                  e164 = normalizeToE164(
                    e.target.value,
                    country.iso2.toUpperCase() as CountryCode
                  )
                }
                if (e164) {
                  setValue(fieldName, e164, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true
                  })
                  setControlledValue(e164)
                  return
                }
                handlePhoneValueChange(e)
              }}
              onBlur={onBlurInput}
              {...inputProps}
            />
          </div>
          <span>{label}</span>
          {onRetry && (
            // type=button prevents form submission
            <button type='button' className='retryButton' onClick={onRetry}>
              <RestartIcon aria-label={t('phone-input.retry-button')} />
            </button>
          )}
        </label>
      </div>
      <InputMessage
        id={fieldName}
        warning={warning}
        focus={focus}
        focusMessage={focusMessage}
      />
    </div>
  )
}
